/** Tela 20 — Administração › Usuários (DEC-12, doc 19 §1). */
import { usePersonalizacao } from '../comercial/personalizacao.tsx';
import { useCallback, useEffect, useState } from 'react';
import { api, consulta, ErroApi } from '../api.ts';
import { useSessao } from '../sessao.tsx';
import { Aviso, Campo, ConfirmarAcao, Modal, Selo, SeloStatus, Vazio } from '../componentes/basicos.tsx';
import { useOpcoesAutenticacao } from '../autenticacao.ts';

interface Usuario {
  id: string;
  nome: string;
  email: string;
  perfil_id: string;
  perfil_nome: string;
  ativo: boolean;
  mfa_habilitado: boolean;
  bloqueado: boolean;
  atualizado_em: string;
}
interface Perfil {
  id: string;
  nome: string;
  status: string;
}

type Dialogo =
  | { tipo: 'novo' }
  | { tipo: 'editar'; u: Usuario }
  | { tipo: 'status'; u: Usuario }
  | { tipo: 'senha'; u: Usuario }
  | { tipo: 'mfa'; u: Usuario }
  | { tipo: 'desbloquear'; u: Usuario }
  | { tipo: 'entra'; u: Usuario }
  | null;

export function TelaUsuarios() {
  const { pode, eu } = useSessao();
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [perfis, setPerfis] = useState<Perfil[]>([]);
  const [busca, setBusca] = useState('');
  const [filtroAtivo, setFiltroAtivo] = useState('');
  const pers = usePersonalizacao('usuarios', { busca: { valor: busca, definir: setBusca }, filtros: { filtroAtivo }, aplicarFiltros: (e: Record<string, unknown>) => setFiltroAtivo((e.filtroAtivo as string) ?? '') });
  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);
  // ADR-007: com o provedor, a senha é da conta (não do HUB); com o Entra, o acesso é pelo vínculo da conta Microsoft 365
  const opcoesAuth = useOpcoesAutenticacao();
  const comProvedor = opcoesAuth?.estrategia === 'provedor';
  const comEntra = opcoesAuth?.provedor === 'entra';

  const carregar = useCallback(async () => {
    try {
      setUsuarios(await api.get(`/api/usuarios${consulta({ busca, ativo: filtroAtivo })}`));
    } catch (e) {
      setErro((e as Error).message);
    }
  }, [busca, filtroAtivo]);

  useEffect(() => {
    carregar();
  }, [carregar]);
  useEffect(() => {
    if (pode('visualizar', 'perfil')) api.get<Perfil[]>('/api/perfis').then(setPerfis).catch(() => undefined);
  }, [pode]);

  async function executar(acao: () => Promise<unknown>, mensagem: string) {
    setErro(null);
    try {
      await acao();
      setDialogo(null);
      setSucesso(mensagem);
      await carregar();
    } catch (e) {
      setErro((e as ErroApi).message);
    }
  }

  const perfisAtivos = perfis.filter((p) => p.status === 'ativo');

  return (
    <section>
      <div className="barra-ferramentas">
        <input type="search" placeholder="Buscar por nome ou e-mail" value={busca} onChange={(e) => setBusca(e.target.value)} aria-label="Buscar usuários" />
        <select value={filtroAtivo} onChange={(e) => setFiltroAtivo(e.target.value)} aria-label="Filtrar por situação">
          <option value="">Todos</option>
          <option value="true">Ativos</option>
          <option value="false">Inativos</option>
        </select>
        <span className="espaco" />
        {pers.controles}
        {pode('criar', 'usuario') && (
          <button className="botao-primario" onClick={() => setDialogo({ tipo: 'novo' })}>
            Novo usuário
          </button>
        )}
      </div>
      {!dialogo && <Aviso>{erro}</Aviso>}
      <Aviso tipo="sucesso">{sucesso}</Aviso>

      {usuarios.length === 0 ? (
        <Vazio>Nenhum usuário encontrado.</Vazio>
      ) : (
        <div className="rolagem quadro-tabela">
        <table ref={pers.tabela} className="tabela">
          <thead>
            <tr>
              <th>Nome</th>
              <th>E-mail</th>
              <th>Perfil</th>
              <th>MFA</th>
              <th>Status</th>
              <th className="col-acoes">Ações</th>
            </tr>
          </thead>
          <tbody>
            {usuarios.map((u) => (
              <tr key={u.id}>
                <td>{u.nome}</td>
                <td>{u.email}</td>
                <td>
                  <Selo tom="destaque">{u.perfil_nome}</Selo>
                </td>
                <td>{u.mfa_habilitado ? <Selo tom="ativo">Habilitado</Selo> : <Selo tom="neutro">Não</Selo>}</td>
                <td>
                  <SeloStatus ativo={u.ativo} /> {u.bloqueado && <Selo tom="alerta">Bloqueado</Selo>}
                </td>
                <td className="col-acoes">
                  {pode('editar', 'usuario') && (
                    <button className="botao-link" onClick={() => setDialogo({ tipo: 'editar', u })}>
                      Editar
                    </button>
                  )}
                  {pode('inativar', 'usuario') && u.id !== eu?.id && (
                    <button className="botao-link" onClick={() => setDialogo({ tipo: 'status', u })}>
                      {u.ativo ? 'Inativar' : 'Reativar'}
                    </button>
                  )}
                  {pode('editar', 'usuario') && (
                    <>
                      {!comProvedor && (
                        <button className="botao-link" onClick={() => setDialogo({ tipo: 'senha', u })}>
                          Redefinir senha
                        </button>
                      )}
                      {comEntra && (
                        <button className="botao-link" onClick={() => setDialogo({ tipo: 'entra', u })} data-teste={`vincular-entra-${u.id}`}>
                          Vincular conta Microsoft 365
                        </button>
                      )}
                      {u.mfa_habilitado && (
                        <button className="botao-link" onClick={() => setDialogo({ tipo: 'mfa', u })}>
                          Redefinir MFA
                        </button>
                      )}
                      {u.bloqueado && (
                        <button className="botao-link" onClick={() => setDialogo({ tipo: 'desbloquear', u })}>
                          Desbloquear
                        </button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {(dialogo?.tipo === 'novo' || dialogo?.tipo === 'editar') && (
        <Modal titulo={dialogo.tipo === 'novo' ? 'Novo usuário' : `Editar ${dialogo.u.nome}`} aoFechar={() => setDialogo(null)}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              const dados = { nome: f.get('nome'), email: f.get('email'), perfil_id: f.get('perfil_id') };
              if (dialogo.tipo === 'novo') {
                executar(
                  () => api.post('/api/usuarios', comProvedor ? dados : { ...dados, senha_inicial: f.get('senha_inicial') }),
                  comEntra ? 'Usuário criado. Vincule a conta Microsoft 365 para liberar o acesso.' : 'Usuário criado.',
                );
              } else {
                executar(
                  () => api.patch(`/api/usuarios/${dialogo.u.id}`, { ...dados, atualizado_em: dialogo.u.atualizado_em, justificativa: f.get('justificativa') || undefined }),
                  'Usuário atualizado.',
                );
              }
            }}
          >
            <Aviso>{erro}</Aviso>
            <Campo rotulo="Nome">{(id) => <input id={id} name="nome" required maxLength={200} defaultValue={dialogo.tipo === 'editar' ? dialogo.u.nome : ''} />}</Campo>
            <Campo rotulo="E-mail">{(id) => <input id={id} name="email" type="email" required defaultValue={dialogo.tipo === 'editar' ? dialogo.u.email : ''} />}</Campo>
            <Campo rotulo="Perfil">
              {(id) => (
                <select id={id} name="perfil_id" required defaultValue={dialogo.tipo === 'editar' ? dialogo.u.perfil_id : ''}>
                  <option value="" disabled>
                    Selecione…
                  </option>
                  {perfisAtivos.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nome}
                    </option>
                  ))}
                </select>
              )}
            </Campo>
            {dialogo.tipo === 'novo' && comProvedor ? (
              <p className="ajuda">Sem senha no HUB: a pessoa entra com a própria conta Microsoft 365, depois que você vincular a conta a este usuário.</p>
            ) : dialogo.tipo === 'novo' ? (
              <Campo rotulo="Senha inicial" ajuda="Mínimo de 12 caracteres. Entregue ao usuário por canal seguro; ele poderá trocá-la em Minha conta.">
                {(id) => <input id={id} name="senha_inicial" type="password" autoComplete="new-password" required minLength={12} />}
              </Campo>
            ) : (
              <Campo rotulo="Justificativa (opcional)">{(id) => <input id={id} name="justificativa" maxLength={1000} />}</Campo>
            )}
            <div className="acoes-form">
              <button type="button" className="botao-secundario" onClick={() => setDialogo(null)}>
                Cancelar
              </button>
              <button className="botao-primario" type="submit">
                Salvar
              </button>
            </div>
          </form>
        </Modal>
      )}

      {dialogo?.tipo === 'status' && (
        <ConfirmarAcao
          erro={erro}
          titulo={dialogo.u.ativo ? 'Inativar usuário' : 'Reativar usuário'}
          descricao={
            dialogo.u.ativo ? (
              <>
                <strong>{dialogo.u.nome}</strong> perderá o acesso imediatamente e todas as sessões abertas serão encerradas. O
                histórico é preservado (não há exclusão — DEC-10).
              </>
            ) : (
              <>
                <strong>{dialogo.u.nome}</strong> voltará a poder entrar no sistema.
              </>
            )
          }
          rotuloConfirmar={dialogo.u.ativo ? 'Inativar' : 'Reativar'}
          aoFechar={() => setDialogo(null)}
          aoConfirmar={(j) =>
            executar(
              () => api.post(`/api/usuarios/${dialogo.u.id}/${dialogo.u.ativo ? 'inativar' : 'reativar'}`, { justificativa: j || undefined }),
              dialogo.u.ativo ? 'Usuário inativado e sessões encerradas.' : 'Usuário reativado.',
            )
          }
        />
      )}

      {dialogo?.tipo === 'senha' && (
        <Modal titulo={`Redefinir senha — ${dialogo.u.nome}`} aoFechar={() => setDialogo(null)}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              executar(
                () => api.post(`/api/usuarios/${dialogo.u.id}/redefinir-senha`, { nova_senha: f.get('nova_senha'), justificativa: f.get('justificativa') || undefined }),
                'Senha redefinida. As sessões do usuário foram encerradas.',
              );
            }}
          >
            <Aviso>{erro}</Aviso>
            <Campo rotulo="Nova senha" ajuda="Mínimo de 12 caracteres.">
              {(id) => <input id={id} name="nova_senha" type="password" autoComplete="new-password" required minLength={12} />}
            </Campo>
            <Campo rotulo="Justificativa (registrada na auditoria)">{(id) => <input id={id} name="justificativa" maxLength={1000} />}</Campo>
            <div className="acoes-form">
              <button type="button" className="botao-secundario" onClick={() => setDialogo(null)}>
                Cancelar
              </button>
              <button className="botao-primario" type="submit">
                Redefinir
              </button>
            </div>
          </form>
        </Modal>
      )}

      {dialogo?.tipo === 'mfa' && (
        <ConfirmarAcao
          erro={erro}
          titulo="Redefinir MFA"
          descricao={<>O autenticador atual de <strong>{dialogo.u.nome}</strong> deixará de valer; um novo cadastro será exigido no próximo login (quando o perfil exigir MFA).</>}
          rotuloConfirmar="Redefinir MFA"
          exigirJustificativa
          aoFechar={() => setDialogo(null)}
          aoConfirmar={(j) => executar(() => api.post(`/api/usuarios/${dialogo.u.id}/redefinir-mfa`, { justificativa: j }), 'MFA redefinido.')}
        />
      )}

      {dialogo?.tipo === 'entra' && (
        <Modal titulo={`Vincular conta Microsoft 365 — ${dialogo.u.nome}`} aoFechar={() => setDialogo(null)}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              executar(
                () => api.post(`/api/usuarios/${dialogo.u.id}/vincular-entra`, { oid: String(f.get('oid') ?? '').trim(), justificativa: f.get('justificativa') }),
                'Conta Microsoft 365 vinculada. As sessões do usuário foram encerradas.',
              );
            }}
          >
            <Aviso>{erro}</Aviso>
            <p className="ajuda">
              O vínculo é pelo <strong>Object ID</strong> da conta no Microsoft Entra ID (centro de administração → Usuários → a pessoa), nunca pelo e-mail. O
              e-mail da conta precisa ser o mesmo cadastrado aqui ({dialogo.u.email}). Um vínculo anterior fica no histórico e não pode ser reaproveitado.
            </p>
            <Campo rotulo="Object ID (oid)">
              {(id) => <input id={id} name="oid" required pattern="[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}" placeholder="00000000-0000-0000-0000-000000000000" data-teste="oid-entra" />}
            </Campo>
            <Campo rotulo="Justificativa (registrada na auditoria)">{(id) => <input id={id} name="justificativa" required maxLength={1000} data-teste="justificativa-entra" />}</Campo>
            <div className="acoes-form">
              <button type="button" className="botao-secundario" onClick={() => setDialogo(null)}>
                Cancelar
              </button>
              <button className="botao-primario" type="submit">
                Vincular
              </button>
            </div>
          </form>
        </Modal>
      )}

      {dialogo?.tipo === 'desbloquear' && (
        <ConfirmarAcao
          erro={erro}
          titulo="Desbloquear acesso"
          descricao={<>Liberar o acesso de <strong>{dialogo.u.nome}</strong>, bloqueado por excesso de tentativas de login?</>}
          rotuloConfirmar="Desbloquear"
          aoFechar={() => setDialogo(null)}
          aoConfirmar={(j) => executar(() => api.post(`/api/usuarios/${dialogo.u.id}/desbloquear`, { justificativa: j || undefined }), 'Acesso desbloqueado.')}
        />
      )}
    </section>
  );
}
